
/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
/* eslint-disable no-multi-spaces */
import {translate} from '../locale/locale';
import {
  centimetersPerSecondToKilometersPerHour,
  centimetersPerSecondToMetersPerSecond,
  centimetersPerSecondToMilesPerHour,
  centimetersToFeet,
  cmToInches,
  feetToCentimeters,
  feetToMeters,
  gallonsToLiters,
  inchesToCm,
  kilogramsToPounds,
  kilometersPerHourToCentimetersPerSecond,
  kilometersPerHourToMetersPerSecond,
  kilometersPerHourToMilesPerHour,
  kilometersToMeters,
  kilometersToMiles,
  litersToGallons,
  metersPerSecondToKilometersPerHour,
  metersPerSecondToMilesPerHour,
  metersToFeet,
  metersToKilometers,
  metersToMiles,
  milesPerHourToCentimetersPerSecond,
  milesPerHourToKilometersPerHour,
  milesPerHourToMetersPerSecond,
  milesToKilometers,
  milesToMeters,
  poundsToKilograms
} from './conversions';
import Unit from './units';

/**
 * Defines the System Of Units entity Attribute that required conversions.
 */
export enum Attribute {
  SPEED     = 'speed',
  ODOMETER  = 'odometer',
  ALTITUDE  = 'altitude',
}

/**
 * Defines the System Of Units mappings between units and Measurement.
 */
export enum Measurement {
  US       = 'mi',
  IMPERIAL = 'mi',
  METRIC   = 'km',
  DEVICE   = 'm',
  MIXED    = 'mi',
  SI       = 'km'
}

/**
 * Defines the statically derived entity attribute conversion mappings
 * as per the CTC Platform systemOfUnits API. The mappings take the
 * shape of FROM > TO. For example, "From ODOMETER TO METRIC", etc.
 */
export const ATTRIBUTE_DISTANCES = {
  [Attribute.ODOMETER] : {
    [Unit.US]       : Measurement.US,
    [Unit.IMPERIAL] : Measurement.IMPERIAL,
    [Unit.METRIC]   : Measurement.METRIC,
    [Unit.DEVICE]   : Measurement.DEVICE,
    [Unit.MIXED]    : Measurement.MIXED,
    [Unit.SI]       : Measurement.SI
  },
  [Attribute.SPEED] : {
    [Unit.US]       : 'mi/h',
    [Unit.IMPERIAL] : 'mi/h',
    [Unit.METRIC]   : 'km/h',
    [Unit.DEVICE]   : 'cm/s',
    [Unit.MIXED]    : 'mi/h',
    [Unit.SI]       : 'm/s'
  },
  [Attribute.ALTITUDE] : {
    [Unit.US]       : 'ft',
    [Unit.IMPERIAL] : 'ft',
    [Unit.METRIC]   : 'm',
    [Unit.DEVICE]   : 'cm',
    [Unit.MIXED]    : 'cm',
    [Unit.SI]       : 'm'
  }
};

/**
 * Defines the odometer attribute conversions for all supported Units.
 * The mappings take the shape of FROM > TO: "From US TO METRIC", etc.
 */
export const ODOMETER_CONVERSIONS = {
  [Unit.US] : {
    [Unit.METRIC] : milesToKilometers
  },
  [Unit.IMPERIAL] : {
    [Unit.METRIC] : milesToKilometers
  },
  [Unit.METRIC] : {
    [Unit.US]       : metersToMiles,
    [Unit.IMPERIAL] : metersToMiles,
    [Unit.METRIC]   : metersToKilometers
  }
};

/**
 * Defines the odometer attribute conversions for all supported Units.
 * The mappings take the shape of FROM > TO: "From US TO METRIC", etc.
 * Used when sending back the odometer values to service.  Metric in
 * service (meters) differs from Metric in UI (kms)
 */
export const REVERSE_ODOMETER_CONVERSIONS = {
  [Unit.US] : {
    [Unit.METRIC] : milesToMeters
  },
  [Unit.IMPERIAL] : {
    [Unit.METRIC] : milesToMeters
  },
  [Unit.METRIC] : {
    [Unit.US]       : kilometersToMiles,
    [Unit.IMPERIAL] : kilometersToMiles,
    [Unit.METRIC]   : kilometersToMeters
  }
};

/**
 * Defines the speed distance conversions for all supported Units.
 * The mappings take the shape of FROM > TO: "From US TO METRIC",
 * etc.
 */
export const DISTANCE_CONVERSIONS = {
  [Unit.US] : {
    [Unit.METRIC] : milesToKilometers
  },
  [Unit.IMPERIAL] : {
    [Unit.METRIC] : milesToKilometers
  },
  [Unit.METRIC] : {
    [Unit.US]       : kilometersToMiles,
    [Unit.IMPERIAL] : kilometersToMiles
  }
};

/**
 * Defines the speed weight conversions for all supported Units.
 * The mappings take the shape of FROM > TO: "From US TO METRIC",
 * etc.
 */
export const WEIGHT_CONVERSIONS = {
  [Unit.US] : {
    [Unit.METRIC] : poundsToKilograms
  },
  [Unit.IMPERIAL] : {
    [Unit.METRIC] : poundsToKilograms
  },
  [Unit.METRIC] : {
    [Unit.US]       : kilogramsToPounds,
    [Unit.IMPERIAL] : kilogramsToPounds
  }
};

/**
 * Defines the speed volume conversions for all supported Units.
 * The mappings take the shape of FROM > TO: "From US TO METRIC",
 * etc.
 */
export const VOLUME_CONVERSIONS = {
  [Unit.US] : {
    [Unit.METRIC] : gallonsToLiters
  },
  [Unit.IMPERIAL] : {
    [Unit.METRIC] : gallonsToLiters
  },
  [Unit.METRIC] : {
    [Unit.US]       : litersToGallons,
    [Unit.IMPERIAL] : litersToGallons
  }
};

/**
 * Defines the speed height conversions for all supported Units.
 * The mappings take the shape of FROM > TO: "From US TO METRIC",
 * etc.
 */
export const HEIGHT_CONVERSIONS = {
  [Unit.US] : {
    [Unit.METRIC] : inchesToCm
  },
  [Unit.IMPERIAL] : {
    [Unit.METRIC] : inchesToCm
  },
  [Unit.METRIC] : {
    [Unit.US]       : cmToInches,
    [Unit.IMPERIAL] : cmToInches
  }
};

/**
 * Defines the speed attribute conversions for all supported Units.
 * The mappings take the shape of FROM > TO: "From US TO METRIC",
 * etc.
 */
export const SPEED_CONVERSIONS = {
  [Unit.US] : {
    [Unit.METRIC] : milesPerHourToKilometersPerHour,
    [Unit.DEVICE] : milesPerHourToCentimetersPerSecond,
    [Unit.SI]     : milesPerHourToMetersPerSecond
  },
  [Unit.IMPERIAL] : {
    [Unit.METRIC] : milesPerHourToKilometersPerHour,
    [Unit.DEVICE] : milesPerHourToCentimetersPerSecond,
    [Unit.SI]     : milesPerHourToMetersPerSecond
  },
  [Unit.METRIC] : {
    [Unit.US]       : kilometersPerHourToMilesPerHour,
    [Unit.IMPERIAL] : kilometersPerHourToMilesPerHour,
    [Unit.DEVICE]   : kilometersPerHourToCentimetersPerSecond,
    [Unit.MIXED]    : kilometersPerHourToMilesPerHour,
    [Unit.SI]       : kilometersPerHourToMetersPerSecond
  },
  [Unit.DEVICE] : {
    [Unit.US]       : centimetersPerSecondToMilesPerHour,
    [Unit.IMPERIAL] : centimetersPerSecondToMilesPerHour,
    [Unit.METRIC]   : centimetersPerSecondToKilometersPerHour,
    [Unit.MIXED]    : centimetersPerSecondToMilesPerHour,
    [Unit.SI]       : centimetersPerSecondToMetersPerSecond
  },
  [Unit.MIXED] : {
    [Unit.METRIC] : milesPerHourToKilometersPerHour,
    [Unit.DEVICE] : milesPerHourToCentimetersPerSecond,
    [Unit.SI]     : milesPerHourToMetersPerSecond
  },
  [Unit.SI] : {
    [Unit.US]       : metersPerSecondToMilesPerHour,
    [Unit.IMPERIAL] : metersPerSecondToMilesPerHour,
    [Unit.METRIC]   : metersPerSecondToKilometersPerHour,
    [Unit.MIXED]    : metersPerSecondToMilesPerHour,
    [Unit.DEVICE]   : (val) => (val * 100)
  }
};

/**
 * Defines the altitude attribute conversions for all supported Units.
 * The mappings take the shape of FROM > TO: "From US TO METRIC", etc.
 */
export const ALTITUDE_CONVERSIONS = {
  [Unit.US] : {
    [Unit.METRIC] : feetToMeters,
    [Unit.MIXED]  : feetToCentimeters,
    [Unit.DEVICE] : feetToCentimeters,
    [Unit.SI]     : feetToMeters
  },
  [Unit.IMPERIAL] : {
    [Unit.METRIC] : feetToMeters,
    [Unit.MIXED]  : feetToCentimeters,
    [Unit.DEVICE] : feetToCentimeters,
    [Unit.SI]     : feetToMeters
  },
  [Unit.METRIC] : {
    [Unit.US]       : metersToFeet,
    [Unit.IMPERIAL] : metersToFeet,
    [Unit.MIXED]    : (val) => (val * 100).toFixed(1),
    [Unit.DEVICE]   : (val) => (val * 100).toFixed(1)
  },
  [Unit.DEVICE] : {
    [Unit.US]       : centimetersToFeet,
    [Unit.IMPERIAL] : centimetersToFeet,
    [Unit.METRIC]   : (val) => (val / 100).toFixed(1),
    [Unit.SI]       : (val) => (val / 100).toFixed(1)
  },
  [Unit.MIXED] : {
    [Unit.US]       : centimetersToFeet,
    [Unit.IMPERIAL] : centimetersToFeet,
    [Unit.METRIC]   : (val) => (val / 100).toFixed(1),
    [Unit.SI]       : (val) => (val / 100).toFixed(1)
  },
  [Unit.SI] : {
    [Unit.US]       : metersToFeet,
    [Unit.IMPERIAL] : metersToFeet,
    [Unit.MIXED]    : (val) => (val * 100).toFixed(1),
    [Unit.DEVICE]   : (val) => (val * 100).toFixed(1)
  }
};

/**
 * Defines the localized labels for each attribute and it's supported
 * System of Units. The mappings take the shape of FROM > TO: "From
 * US TO METRIC", etc.
 *
 * @private
 */
const LABELS = {
  [Attribute.ODOMETER] : {
    [Unit.US]       : 'odometerMiles',
    [Unit.IMPERIAL] : 'odometerMiles',
    [Unit.METRIC]   : 'odometerKM'
  },
  [Attribute.SPEED] : {
    [Unit.US]       : 'speedMPH',
    [Unit.IMPERIAL] : 'speedMPH',
    [Unit.METRIC]   : 'speedKMPH'
  },
  [Attribute.ALTITUDE] : {
    [Unit.US]       : 'altitudeFT',
    [Unit.IMPERIAL] : 'altitudeFT',
    [Unit.METRIC]   : 'altitudeMeters'
  }
};

/**
 * Given an attribute name, determines if the attribute is a supported,
 * valid attribute used within the conversions. Valid values are defined
 * by the SPEED, ODOMETER, and ALTITUDE enums.
 *
 * @method isValidAttribute
 * @param  {string} attr The attribute against which to test for validity.
 */
export const isValidAttribute = (attr: string): boolean => {
  return (
    attr === Attribute.SPEED ||
    attr === Attribute.ODOMETER ||
    attr === Attribute.ALTITUDE
  );
};

/**
 * Given a specific Unit, returns the conversion measurement for the given
 * attribute.
 *
 * @method  getUnitByAttribute
 * @param   {string} attribute attribute for which the result is to returned.
 * @param   {string} uom The unit of measure for which the attribute is to
 *          be converted.
 * @returns {string} The resulting label, or "N/A" if not supported.
 */
export const getUnitByAttribute = (attribute: Attribute, uom: Unit): Measurement | string => {
  let measure;
  if (isValidAttribute(attribute)) {
    measure = ATTRIBUTE_DISTANCES[attribute][uom];
    if (!measure) {
      return `Invalid unit "${uom}" specified.`;
    }
  } else {
    return `Invalid attribute "${attribute}" specified.`;
  }
  return measure;
};

/**
 * Provides the translations for a given Entity attribute based on the
 * provided Unit of Measure.
 *
 * @method  getAttributeLabel
 * @param   {string} attribute The attribute for which the label is to
 *          be returned.
 * @param   {string} uom The Unit of measure for which the label is t
 *          be determined.
 * @returns {string} The resulting label, or "N/A" if not supported.
 */
export const getAttributeLabel = (attribute: Attribute, uom: Unit, pathOnly = false): string => {
  let key;
  if (isValidAttribute(attribute)) {
    key = LABELS[attribute][uom];
    if (key) {
      const path = `common:units.measurements.${key}`;
      return !pathOnly ? translate(path) : path;
    }
  }
  return 'N/A';
};
